import React, { useState, useEffect, useCallback } from "react";
import { postEmployeeOrder, fetchEmployees, fetchCustomers } from "../../Utils/api";
import { useAuth } from "../../Auth/AuthContext";
import {
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Paper,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Breadcrumbs,
  Link,
  CircularProgress,
  Autocomplete
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import WorkIcon from "@mui/icons-material/Work";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import PopupGeneric from "../../Components/PopupGeneric/pop-up-generic";
import { useNavigate } from "react-router";
import { validateAddress, validateCityState, validateEmail, validateName, validatePersonDetails, validatePhone, validateZip } from "../../Utils/validations";
import { US_STATES } from "../../Utils/constants";

const AddEmployee = () => {
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isPopupVisible, setPopupVisible] = useState(false);
  const [popupTitle, setPopupTitle] = useState("");
  const [popupMessage, setPopupMessage] = useState("");
  const [isCancelPopupVisible, setCancelPopupVisible] = useState(false);
  const navigate = useNavigate();
  const { companyId } = useAuth();
  const [existingEmployees, setExistingEmployees] = useState([]);
  const [existingCustomers, setExistingCustomers] = useState([]);
  const [emailError, setEmailError] = useState("");

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
    position: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
  });

  const prefix = "+1";

  // Load existing data function
  const loadExistingData = useCallback(async () => {
    try {
      const [employees, customers] = await Promise.all([
        fetchEmployees(companyId, null, null),
        fetchCustomers(companyId, null, null)
      ]);
      setExistingEmployees(employees || []);
      setExistingCustomers(customers || []);
    } catch (error) {
      console.error('Error fetching existing employees/customers:', error);
    }
  }, [companyId]);

  // Fetch existing employees and customers on component mount
  useEffect(() => {
    if (companyId) {
      loadExistingData();
    }
  }, [companyId, loadExistingData]);

  // Check if email is already used
  const checkEmailDuplicate = (email) => {
    if (!email || !email.trim()) {
      setEmailError("");
      return false;
    }

    const emailLower = email.toLowerCase().trim();

    // Check in employees
    const employeeExists = existingEmployees.some(emp =>
      emp.email && emp.email.toLowerCase().trim() === emailLower
    );

    // Check in customers
    const customerExists = existingCustomers.some(cust =>
      cust.custEmail && cust.custEmail.toLowerCase().trim() === emailLower
    );

    if (employeeExists) {
      setEmailError("This email is already used by an existing employee");
      return true;
    }

    if (customerExists) {
      setEmailError("This email is already used by an existing customer");
      return true;
    }

    setEmailError("");
    return false;
  };

  // Updates form data
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Check email duplicate when email field changes
    if (name === 'email') {
      checkEmailDuplicate(value);
    }
  };

  useEffect(() => {
    const isValid = validatePersonDetails(formData) && formData.position.trim() && formData.address.trim() && !emailError;
    setIsSubmitDisabled(!isValid);
  }, [formData, emailError]);


  // Handle form submission
  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const employeeData = {
        username: formData.email,
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        contactNumber:prefix+ formData.phoneNumber,
        empDesignation: formData.position,
        empAddress: formData.address,
        empCity: formData.city,
        empState: formData.state,
        empZipCode: formData.zipCode,
        companyId: companyId,
        currRecInd: "Y"
      };

      await postEmployeeOrder(employeeData);
      setPopupTitle("Success");
      setPopupMessage("Employee creation successful");
      setPopupVisible(true);
      resetForm();
      // Refresh the employee list to include the newly created employee
      await loadExistingData();
       setTimeout(() => {
      navigate('/dash',  { replace: true });
       }, 2000);
    } catch (error) {
      setPopupTitle("Alert");
      const errorMessage = error.response?.data?.message || error.message || "Failed to submit Employee order. Please try again.";
      setPopupMessage(errorMessage);
      setPopupVisible(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    const anyFieldFilled = Object.values(formData).some((v) => v !== "");
    if (anyFieldFilled) {
      setCancelPopupVisible(true);
    } else {
      navigate(-1);
    }
  };

  // Reset form after submission
  const resetForm = () => {
    setFormData({
      firstName: "",
      lastName: "",
      phoneNumber: "",
      email: "",
      position: "",
      address: "",
      city: "",
      state: "",
      zipCode: "",
    });
    setEmailError("");
  };

  return (
    <Box sx={{ padding: "20px", paddingTop: "120px", paddingBottom: "40px" }}>
      <Box sx={{ maxWidth: "900px", margin: "0 auto" }}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ marginBottom: "20px", paddingLeft: "10px" }}
        >
          <Link
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            color="inherit"
            onClick={() => navigate('/dash')}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Link
            underline="hover"
            sx={{ cursor: 'pointer' }}
            color="inherit"
            onClick={() => navigate('/view-emp')}
          >
            Manage Employees
          </Link>
          <Typography color="text.primary" sx={{ fontWeight: 'bold' }}>
            Add Employee
          </Typography>
        </Breadcrumbs>

        <Paper elevation={3} sx={{ padding: "16px", textAlign: "center" }}>
        <Typography variant="h5" sx={{ fontWeight: "bold", marginBottom: "16px" }}>
          Add Employee
        </Typography>

        <Grid container spacing={2}>
          {/* First Name */}
          <Grid item xs={6}>
            <TextField
              label="First Name"
              name="firstName"
              required
              fullWidth
              value={formData.firstName}
              onChange={(e) => {
                const value = e.target.value;
                if(validateName(value).validChars) handleChange(e);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon />
                  </InputAdornment>
                ),
              }}
              helperText="Only letters and spaces allowed."
            />
          </Grid>

          {/* Last Name */}
          <Grid item xs={6}>
            <TextField
              label="Last Name"
              name="lastName"
              required
              fullWidth
              value={formData.lastName}
              onChange={(e) => {
                const value = e.target.value;
                if(validateName(value).validChars) handleChange(e);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon />
                  </InputAdornment>
                ),
              }}
              helperText="Only letters and spaces allowed."
            />
          </Grid>

          {/* Phone Number */}
          <Grid item xs={6}>
            <TextField
              label="Phone Number"
              name="phoneNumber"
              required
              fullWidth
              value={formData.phoneNumber}
              onChange={(e) => {
                const value = e.target.value;
                if(validatePhone(value).validChars) handleChange(e);
              }}
              error={formData.phoneNumber.length > 0 && formData.phoneNumber.length !== 10}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon />
                  </InputAdornment>
                ),
              }}
              helperText="10-digit number only."
            />
          </Grid>

          {/* Email Address */}
          <Grid item xs={6}>
            <TextField
              label="Email Address"
              name="email"
              required
              fullWidth
              value={formData.email}
              onChange={(e) => {
                const value = e.target.value;
                if(validateEmail(value).validChars) handleChange(e);
              }}
              onBlur={(e) => {
                const value = e.target.value.trim();
                if (value && !validateEmail(value).isValid) {
                    console.error("Invalid email format");
                }
              }}
              InputProps={{
                  startAdornment: (
                      <InputAdornment position="start">
                          <EmailIcon />
                      </InputAdornment>
                  ),
              }}
              error={(formData.email && !validateEmail(formData.email).isValid) || !!emailError}
              helperText={
                emailError ||
                "Enter valid email address, you will receive an email after successful profile creation."
              }
            />
          </Grid>

          {/* Position */}
          <Grid item xs={6}>
            <FormControl fullWidth required>
              <InputLabel id="position-label">
                Designation
              </InputLabel>
              <Select
                labelId="position-label"
                label="Designation"
                name="position"
                required
                value={formData.position}
                onChange={handleChange}
                sx={{ textAlign: "left" }} // Ensures left alignment
                startAdornment={
                  <InputAdornment position="start">
                    <WorkIcon />
                  </InputAdornment>
                }
              >
                <MenuItem value="Owner">Owner</MenuItem>
                <MenuItem value="Manager">Manager</MenuItem>
                <MenuItem value="Technician">Technician</MenuItem>
                <MenuItem value="Partner">Partner</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Employee Address */}
          <Grid item xs={12}>
            <TextField
              label="Address"
              name="address"
              required
              fullWidth
              value={formData.address}
              onChange={(e) => {
                const value = e.target.value;
                if(validateAddress(value).validChars) handleChange(e);
              }}
              InputProps={{ startAdornment: (<InputAdornment position="start"><HomeIcon /></InputAdornment>) }}
              helperText="Only letters, numbers, spaces, and # allowed."
            />
          </Grid>

          {/* Employee City */}
          <Grid item xs={6}>
            <TextField 
              label="City" 
              name="city" 
              required 
              fullWidth 
              value={formData.city} 
              onChange={(e) => {
                const value = e.target.value;
                if(validateCityState(value).validChars) handleChange(e);
              }}
              InputProps={{ startAdornment: (<InputAdornment position="start"><LocationOnIcon /></InputAdornment>) }} 
              helperText="Only letters and spaces allowed."
            />
          </Grid>

          {/* Employee State */}
          <Grid item xs={6}>
            <Autocomplete
              options={US_STATES}
              value={formData.state}
              onChange={(event, newValue) => {
                const syntheticEvent = {
                  target: {
                    name: 'state',
                    value: newValue || '',
                  },
                };
                handleChange(syntheticEvent);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="State"
                  name="state"
                  required
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <LocationOnIcon />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                  helperText="Select a state from the list."
                />
              )}
            />
          </Grid>

          {/* Employee Zip Code */}
          <Grid item xs={6}>
            <TextField
              label="Zip Code"
              name="zipCode"
              required
              fullWidth
              value={formData.zipCode}
              onChange={(e) => {
                const value = e.target.value;
                if(validateZip(value).validChars) handleChange(e);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOnIcon />
                  </InputAdornment>
                ),
              }}
              helperText="5-digit number only."
            />
          </Grid>
        </Grid>

        {/* Submit Button */}
        <Button
          variant="contained"
          sx={{ marginTop: "16px", width: "100%", padding: "10px", fontWeight: "bold" }}
          onClick={handleSubmit}
          disabled={isSubmitDisabled || isLoading}
          startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {isLoading ? "Submitting..." : "Submit"}
        </Button>

        {/* Cancel Button */}
        <Button
          variant="outlined"
          sx={{ marginTop: "8px", width: "100%", padding: "10px", fontWeight: "bold" }}
          onClick={handleCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
      </Paper>
      {/* Popup Modal for Alerts */}
      <PopupGeneric
        isVisible={isPopupVisible}
        onClose={() => setPopupVisible(false)}
        title={popupTitle}
        content={<p>{popupMessage}</p>}
        actions={
          <button
            onClick={() => setPopupVisible(false)}
            style={{
              marginTop: '10px',
              padding: '5px 10px',
              borderRadius: '5px',
              backgroundColor: '#003366',
              color: '#fff',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            OK
          </button>
        }
      />
      {/* Cancel Confirmation Popup */}
      <PopupGeneric
        isVisible={isCancelPopupVisible}
        onClose={() => {}}
        title="Unsaved Changes"
        content={
          <div>
            <p>You have unsaved employee details.</p>
            <p>If you leave, all entered information will be lost.</p>
          </div>
        }
        actions={
          <div style={{ display: "flex", gap: "1rem", justifyContent: "center" }}>
            <button
              onClick={() => {
                setCancelPopupVisible(false);
                navigate(-1);
              }}
              style={{
                padding: "8px 16px",
                borderRadius: "5px",
                backgroundColor: "#8f0c03",
                color: "#fff",
                border: "none",
                cursor: "pointer",
              }}
            >
              Leave
            </button>
            <button
              onClick={() => setCancelPopupVisible(false)}
              style={{
                padding: "8px 16px",
                borderRadius: "5px",
                backgroundColor: "#1d5ca7",
                border: "none",
                color: "#fff",
                cursor: "pointer",
              }}
            >
              Stay on Page
            </button>
          </div>
        }
      />
      </Box>
    </Box>
  );
};

export default AddEmployee;